/**
 * Schedule Browser
 *
 * Left sidebar panel showing all schedules in the model, organized by category.
 * Supports search, filtering, and schedule selection.
 */

import { useState, useCallback, useMemo } from 'react';
import type { ParsedSchedule, ScheduleCategory, ScheduleInfo } from './types';
import { useScheduleStore } from './scheduleStore';
import { useIDFStore } from '../stores/idfStore';
import { FileRequiredEmptyState } from '@/components/FileRequiredEmptyState';

/**
 * Category display names
 */
const CATEGORY_NAMES: Record<ScheduleCategory, string> = {
  occupancy: 'Occupancy',
  lighting: 'Lighting',
  equipment: 'Equipment',
  heating: 'Heating',
  cooling: 'Cooling',
  thermostat: 'Thermostat',
  hvac: 'HVAC',
  ventilation: 'Ventilation',
  activity: 'Activity',
  availability: 'Availability',
  other: 'Other',
};

/**
 * Category icons
 */
function CategoryIcon({ category }: { category: ScheduleCategory }) {
  const className = 'h-4 w-4';

  switch (category) {
    case 'occupancy':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      );
    case 'lighting':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z" />
        </svg>
      );
    case 'equipment':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
        </svg>
      );
    case 'heating':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 23c3.9 0 7-3.1 7-7 0-2.8-1.6-4.7-2.8-6.4C14.5 7.3 13 5.1 13 2c0-.6-.4-1-1-1-.5 0-.9.4-1 .9C10.6 5.5 8 8.3 6.4 11 5.5 12.4 5 14.1 5 16c0 3.9 3.1 7 7 7z" />
        </svg>
      );
    case 'cooling':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22v-2z" />
        </svg>
      );
    case 'thermostat':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M15 13V5c0-1.66-1.34-3-3-3S9 3.34 9 5v8c-1.21.91-2 2.37-2 4 0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.63-.79-3.09-2-4zm-4-8c0-.55.45-1 1-1s1 .45 1 1h-1v1h1v2h-1v1h1v2h-2V5z" />
        </svg>
      );
    case 'hvac':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 11c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-1-7v4l4 2-4 2v4h2v-3l3-1.5-3-1.5V4h-2z" />
        </svg>
      );
    case 'ventilation':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M9.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5.75 8.9L3 23h2.1l1.75-8L9 17v6h2v-7.55L8.95 13.4l.6-3C10.85 12 12.8 13 15 13v-2c-1.85 0-3.45-1-4.35-2.45l-.95-1.6C9.35 6.35 8.7 6 8 6c-.25 0-.5.05-.75.15L2 8.3V13h2V9.65l1.75-.75" />
        </svg>
      );
    case 'activity':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7" />
        </svg>
      );
    default:
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
        </svg>
      );
  }
}

/**
 * Highlight matching text in a string
 */
function HighlightText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);
  const lowerQuery = query.toLowerCase();

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === lowerQuery ? (
          <mark key={i} className="rounded-sm bg-yellow-200 px-0.5 dark:bg-yellow-800 dark:text-yellow-100">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

/**
 * Schedule item in the browser
 */
function ScheduleItem({
  schedule,
  info,
  selected,
  onClick,
  searchQuery = '',
}: {
  schedule: ParsedSchedule;
  info: ScheduleInfo;
  selected: boolean;
  onClick: () => void;
  searchQuery?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 ${
        selected
          ? 'bg-primary/10 text-primary dark:bg-primary/20'
          : 'hover:bg-muted text-foreground'
      }`}
      role="option"
      aria-selected={selected}
    >
      <div className="flex-1 overflow-hidden">
        <div className="truncate text-sm font-medium">
          <HighlightText text={schedule.name} query={searchQuery} />
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>
            <HighlightText text={schedule.scheduleType.replace('Schedule:', '')} query={searchQuery} />
          </span>
          {info.usageCount > 0 && (
            <>
              <span>·</span>
              <span>{info.usageCount} ref{info.usageCount !== 1 ? 's' : ''}</span>
            </>
          )}
        </div>
      </div>
      {selected && (
        <svg className="h-4 w-4 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
        </svg>
      )}
    </button>
  );
}

/**
 * Category section in the browser
 */
function CategorySection({
  category,
  schedules,
  getScheduleInfo,
  selectedId,
  onSelect,
  expanded,
  onToggle,
}: {
  category: ScheduleCategory;
  schedules: ParsedSchedule[];
  getScheduleInfo: (schedule: ParsedSchedule) => ScheduleInfo;
  selectedId: string | null;
  onSelect: (id: string) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (schedules.length === 0) return null;

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
        aria-expanded={expanded}
        aria-controls={`category-${category}`}
      >
        <CategoryIcon category={category} />
        <span className="flex-1 text-sm font-medium text-foreground">
          {CATEGORY_NAMES[category]}
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>{schedules.length}</span>
          <svg
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
          </svg>
        </span>
      </button>

      {expanded && (
        <div id={`category-${category}`} className="flex flex-col gap-1 px-2 pb-2" role="listbox" aria-label={`${CATEGORY_NAMES[category]} schedules`}>
          {schedules.map((schedule) => (
            <ScheduleItem
              key={schedule.id}
              schedule={schedule}
              info={getScheduleInfo(schedule)}
              selected={schedule.id === selectedId}
              onClick={() => onSelect(schedule.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Schedule Browser Props
 */
interface ScheduleBrowserProps {
  onNewSchedule?: () => void;
  onImportTemplate?: () => void;
  /** Called when a schedule is selected (useful for closing mobile drawers) */
  onScheduleSelect?: () => void;
}

/**
 * Schedule Browser Component
 */
export function ScheduleBrowser({ onNewSchedule, onImportTemplate, onScheduleSelect }: ScheduleBrowserProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<ScheduleCategory>>(
    new Set(['occupancy', 'lighting', 'heating', 'cooling', 'hvac'])
  );

  const schedules = useScheduleStore((state) => state.schedules);
  const selectedScheduleId = useScheduleStore((state) => state.selectedScheduleId);
  const selectSchedule = useScheduleStore((state) => state.selectSchedule);
  const getScheduleInfo = useScheduleStore((state) => state.getScheduleInfo);
  const document = useIDFStore((state) => state.document);

  const toggleCategory = useCallback((category: ScheduleCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Get info and group schedules by category
  const schedulesByCategory = useMemo(() => {
    const grouped = new Map<ScheduleCategory, ParsedSchedule[]>();

    for (const schedule of schedules) {
      const info = getScheduleInfo(schedule, document);
      const existing = grouped.get(info.category) || [];
      grouped.set(info.category, [...existing, schedule]);
    }

    return grouped;
  }, [schedules, getScheduleInfo, document]);

  // Filter schedules by search query
  const filteredSchedules = useMemo(() => {
    if (!searchQuery.trim()) {
      return null; // Show categorized view
    }

    const query = searchQuery.toLowerCase();
    return schedules.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.scheduleType.toLowerCase().includes(query) ||
        (s.typeLimits?.toLowerCase().includes(query) ?? false)
    );
  }, [schedules, searchQuery]);

  // Category order for display
  const categoryOrder: ScheduleCategory[] = [
    'occupancy',
    'lighting',
    'equipment',
    'heating',
    'cooling',
    'thermostat',
    'hvac',
    'ventilation',
    'activity',
    'availability',
    'other',
  ];

  return (
    <div className="flex h-full flex-col bg-muted/30">
      {/* Header */}
      <div className="border-b border-border bg-card px-3 py-2">
        <h2 className="text-sm font-semibold text-foreground">Schedules</h2>
        <p className="text-xs text-muted-foreground">{schedules.length} schedule{schedules.length !== 1 ? 's' : ''} in model</p>
      </div>

      {/* Search */}
      <div className="border-b border-border bg-card p-2">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search schedules..."
            className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-3 text-sm text-foreground placeholder-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <svg
            className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Schedule list */}
      <div className="flex-1 overflow-y-auto">
        {filteredSchedules ? (
          // Search results view
          <div className="p-2">
            {filteredSchedules.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">No schedules found</div>
            ) : (
              <div className="flex flex-col gap-1" role="listbox" aria-label="Search results">
                <div className="px-2 py-1 text-xs text-muted-foreground">
                  {filteredSchedules.length} result{filteredSchedules.length !== 1 ? 's' : ''}
                </div>
                {filteredSchedules.map((schedule) => (
                  <ScheduleItem
                    key={schedule.id}
                    schedule={schedule}
                    info={getScheduleInfo(schedule, document)}
                    selected={schedule.id === selectedScheduleId}
                    onClick={() => {
                      selectSchedule(schedule.id);
                      onScheduleSelect?.();
                    }}
                    searchQuery={searchQuery}
                  />
                ))}
              </div>
            )}
          </div>
        ) : schedules.length === 0 ? (
          // Empty state - only show action if handler is provided
          onNewSchedule ? (
            <FileRequiredEmptyState
              reason="no-schedules"
              action={{
                label: 'New Schedule',
                onClick: onNewSchedule,
              }}
              {...(onImportTemplate ? {
                secondaryAction: {
                  label: 'Import Template',
                  onClick: onImportTemplate,
                }
              } : {})}
            />
          ) : (
            <FileRequiredEmptyState
              reason="no-schedules"
              {...(onImportTemplate ? {
                action: {
                  label: 'Import Template',
                  onClick: onImportTemplate,
                }
              } : {})}
            />
          )
        ) : (
          // Categorized view
          <div>
            {categoryOrder.map((category) => {
              const categorySchedules = schedulesByCategory.get(category) || [];
              return (
                <CategorySection
                  key={category}
                  category={category}
                  schedules={categorySchedules}
                  getScheduleInfo={(s) => getScheduleInfo(s, document)}
                  selectedId={selectedScheduleId}
                  onSelect={(id) => {
                    selectSchedule(id);
                    onScheduleSelect?.();
                  }}
                  expanded={expandedCategories.has(category)}
                  onToggle={() => toggleCategory(category)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="border-t border-border bg-card p-2">
        <div className="flex gap-2">
          <button
            onClick={onNewSchedule}
            className="flex flex-1 items-center justify-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            New
          </button>
          <button
            onClick={onImportTemplate}
            className="flex flex-1 items-center justify-center gap-1 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
            </svg>
            Template
          </button>
        </div>
      </div>
    </div>
  );
}
